import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { doc, serverTimestamp, updateDoc, deleteField } from 'firebase/firestore';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { CopyLinkButton } from '../common/CopyLinkButton';
import { buildShareUrl } from '../../services/shareLinks';
import { Badge } from '../common/Badge';
import { useManufacturerCatalogueChanged } from '../../hooks/useManufacturerCatalogue';
import { invalidateDocsLinks } from '../../hooks/useDocsLinks';
import { StatusBadge } from '../common/StatusBadge';
import { SortIcon } from '../common/SortIcon';
import { db } from '../../services/firebase';
import { normalizeCalibreKey } from '../../services/calibreResolution';
import {
  DEFAULT_MANUFACTURER_SORT,
  nextManufacturerSort,
  sortManufacturers,
  sortManufacturersByName,
  type ManufacturerSort,
  type ManufacturerSortField,
} from '../../services/manufacturerCatalogueQuery';
import {
  getCalibreCountsByManufacturer,
  isAlgoliaConfigured,
} from '../../services/algolia';
import type { MovementManufacturer, WatchBrand } from '../../types';
import {
  bulkSetManufacturerGeneric,
  getCustomCalibres,
  getWatchBrands,
  type CalibreAliasDetail,
  type CustomCalibreEntry,
  callAuditCalibresMissingManufacturerId,
  callAuditCalibresWithUnresolvedBrandId,
  callAuditRefsUnresolvedCalibre,
  callBackfillBrandsToAlgolia,
  callBackfillCalibresToAlgolia,
  callBackfillManufacturersToAlgolia,
  callDeleteMovementManufacturers,
  callMergeMovementManufacturers,
  callPushAlgoliaBrandIndexSettings,
  callPushAlgoliaCalibreIndexSettings,
  callPushAlgoliaManufacturerIndexSettings,
  callPushAlgoliaRefIndexSettings,
  callResyncAllReferencesToAlgolia,
  createMovementManufacturer,
  deriveManufacturerSlug,
  resolveLogoToStorage,
  resolveHeroToStorage,
  LogoFetchError,
  deleteMovementManufacturer,
  getCalibreCountsByManufacturerId,
  getManufacturerCatalogue,
  getMovementManufacturerById,
  invalidateManufacturerIndex,
  updateMovementManufacturer,
  type CalibresUnresolvedBrandIdAuditResult,
  type CalibreManufacturerIdAuditResult,
  type EntityIndexBackfillResult,
  type EntityIndexSettingsResult,
  type ManufacturerCatalogueEntry,
  type PushAlgoliaSettingsResult,
  type RefsUnresolvedCalibreAuditResult,
} from '../../services/firebase';
import {
  MovementManufacturerDetail,
  type MovementManufacturerDraft,
} from './MovementManufacturerDetail';
import { ManufacturerDataHealthOverview } from './ManufacturerDataHealthOverview';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { CalibreActionToolbar } from '../CuratedWatches/CalibreActionToolbar';
import { CustomCalibreModal } from '../CuratedWatches/CustomCalibreModal';
import { useParentOrgIndex } from '../ParentOrganizations/useParentOrgIndex';
import {
  matchesParentOrgFilter,
  parentOrgChips,
  resolutionLabel,
  resolveParentOrgForManufacturer,
  UNSET_PARENT_ORG_FILTER,
  type ParentOrgIndex,
} from '../ParentOrganizations/parentOrgResolve';

type ModalMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; manufacturer: MovementManufacturer };

// Rows of the unified "Resync to Algolia" panel. `references` rebuilds the
// watch_references index; the other three rebuild the entity indexes.
const RESYNC_INDEXES = [
  { key: 'references', label: 'References' },
  { key: 'brands', label: 'Brands' },
  { key: 'manufacturers', label: 'Manufacturers' },
  { key: 'calibres', label: 'Calibres' },
] as const;

type ResyncIndexKey = (typeof RESYNC_INDEXES)[number]['key'];

interface MovementManufacturerEditorProps {
  /**
   * Deep-link target: open this manufacturer's editor on arrival. Set by another
   * tab (the parent-org Portfolio chips); cleared through `onOpenManufacturerConsumed`
   * so a later visit to the tab doesn't re-open it.
   */
  openManufacturerId?: string;
  onOpenManufacturerConsumed?: () => void;
}

export function MovementManufacturerEditor({
  openManufacturerId,
  onOpenManufacturerConsumed,
}: MovementManufacturerEditorProps = {}) {
  const showCalibreMissingManufacturerIdAudit = useFeatureFlag(
    'tools.showCalibreMissingManufacturerIdAudit',
  );
  const showRefsUnresolvedCalibreAudit = useFeatureFlag(
    'tools.showRefsUnresolvedCalibreAudit',
  );
  const showPushAlgoliaSettings = useFeatureFlag(
    'tools.showPushAlgoliaSettings',
  );
  const showResyncToAlgolia = useFeatureFlag('tools.showResyncToAlgolia');
  const showCalibresUnresolvedBrandIdAudit = useFeatureFlag(
    'tools.showCalibresUnresolvedBrandIdAudit',
  );
  const showAkaBrandIdShapeAudit = useFeatureFlag(
    'tools.showAkaBrandIdShapeAudit',
  );
  const showEntityIndexTools = useFeatureFlag('tools.showEntityIndexTools');
  const showManufacturerDataHealth = useFeatureFlag('tools.showManufacturerDataHealth');
  // Which entity-index action is currently running (null = idle).
  const [entityIndexBusy, setEntityIndexBusy] = useState<string | null>(null);
  const [manufacturers, setManufacturers] = useState<
    ManufacturerCatalogueEntry[]
  >([]);
  /** Which row's Edit button is mid-hydration (null = none). */
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [calibreCounts, setCalibreCounts] = useState<Map<string, number>>(new Map());
  // Parent organizations from `parent_organizations`, not the lookup — so a
  // renamed org shows its new name. Catalogue rows carry `parentOrgId`, so the
  // Parent column and the chips resolve by id; the legacy string is only the
  // fallback for a manufacturer that was never linked.
  const {
    index: parentOrgIndex,
    loading: parentOrgsLoading,
    error: parentOrgsError,
    reload: reloadParentOrgs,
  } = useParentOrgIndex({ autoLoad: false }); // `refresh` loads it, on mount and on Refresh
  /** Parent-org chip filter: `org:<id>`, `raw:<string>`, `Unset`, or none. */
  const [parentOrgFilter, setParentOrgFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'generic' | 'in-house'>('all');
  const [includeFilter, setIncludeFilter] = useState<'all' | 'empty' | 'set'>('all');
  /**
   * Listing order, set from the column headers. Name A→Z by default, as it has
   * always been; Created (newest first on its first click) is the one that finds
   * a manufacturer just made by a quick-create elsewhere.
   */
  const [sortOrder, setSortOrder] = useState<ManufacturerSort>(DEFAULT_MANUFACTURER_SORT);
  const [modalMode, setModalMode] = useState<ModalMode>({ kind: 'closed' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<ManufacturerCatalogueEntry | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [refsAuditOpen, setRefsAuditOpen] = useState(false);
  const [calibresUnresolvedBrandIdAuditOpen, setCalibresUnresolvedBrandIdAuditOpen] = useState(false);
  const [akaBrandIdAuditOpen, setAkaBrandIdAuditOpen] = useState(false);
  const [dataHealthOpen, setDataHealthOpen] = useState(false);
  const [pushAlgoliaSettingsOpen, setPushAlgoliaSettingsOpen] = useState(false);
  const [resyncOpen, setResyncOpen] = useState(false);
  const [resyncConfirmText, setResyncConfirmText] = useState('');
  const [resyncRunning, setResyncRunning] = useState(false);
  const [resyncInclude, setResyncInclude] = useState<Record<ResyncIndexKey, boolean>>({
    references: true,
    brands: true,
    manufacturers: true,
    calibres: true,
  });
  const [resyncFull, setResyncFull] = useState<Record<ResyncIndexKey, boolean>>({
    references: false,
    brands: false,
    manufacturers: false,
    calibres: false,
  });
  const [resyncSummary, setResyncSummary] = useState<string[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeCanonicalId, setMergeCanonicalId] = useState<string | null>(null);
  const [mergeRunning, setMergeRunning] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteRunning, setBulkDeleteRunning] = useState(false);

  /**
   * Per-row calibre counts, from the `manufacturerId` facet on
   * `watchkeeper_calibres` — one search that returns no hits, just the counts
   * the index already maintains.
   *
   * This used to be `getCalibreCountsByManufacturerId()`, a
   * `getDocs(custom_calibres)` scan of ~1,822 documents run on every mount of
   * this tab purely to render one number per row. The Firestore version is kept
   * as the fallback: a missing count would read as "no calibres", which is a
   * wrong answer rather than a slow one.
   */
  const loadCalibreCounts = useCallback(async (): Promise<
    Map<string, number>
  > => {
    if (!isAlgoliaConfigured()) return getCalibreCountsByManufacturerId();
    try {
      return await getCalibreCountsByManufacturer();
    } catch (error) {
      console.warn(
        'Calibre facet counts unavailable, falling back to a full scan',
        error,
      );
      return getCalibreCountsByManufacturerId();
    }
  }, []);

  /**
   * The listing, on Algolia like every other manufacturer surface.
   *
   * This was the last `getMovementManufacturers()` caller — a whole-collection
   * read on every mount of this tab — and it stayed one until watchlock #867
   * indexed the five curation fields it renders for EVERY row: `status` and
   * `heritageBrandIds` (the lifecycle pill and ⚘ Heritage badge), plus
   * `brandIdsManualInclude` / `brandIdsDerivedFromRefs` / `brandExclusions`,
   * which between them drive the Brand-include filter, the "Needs curation"
   * count and every number the Data Health overview computes. Because they are
   * read per-row rather than for a handful, there was no targeted top-up that
   * could beat the collection read the way the brand listing's four queries do.
   *
   * Rows are a PROJECTION now, not whole documents — see
   * `MANUFACTURER_FIELDS_NOT_INDEXED` for the six the index omits, and
   * {@link openEdit} for the single-document hydrate that keeps the edit form
   * from round-tripping a projection back over them.
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [all, counts] = await Promise.all([
        getManufacturerCatalogue(),
        loadCalibreCounts(),
        reloadParentOrgs(),
      ]);
      setManufacturers(all);
      setCalibreCounts(counts);
    } catch (err) {
      console.error('Failed to load manufacturers', err);
      toast.error('Failed to load manufacturers');
    } finally {
      setLoading(false);
    }
  }, [loadCalibreCounts, reloadParentOrgs]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A manufacturer created or deleted from ANOTHER surface — the calibre
  // editor's quick-create, the module editor's — belongs in this listing without
  // a page reload. The overlay makes the refresh correct immediately rather than
  // whenever the Algolia trigger gets there. See
  // `services/manufacturerCatalogueOverlay.ts`.
  useManufacturerCatalogueChanged(() => void refresh());

  /**
   * Overwrite one row with its authoritative Firestore document.
   *
   * Called after a save, because the Algolia record is written by a trigger and
   * lags the document by a moment — {@link refresh} alone can therefore replay
   * the pre-edit values for the very manufacturer the admin just changed, which
   * reads as a failed write. A row rebuilt from the document can never be stale.
   *
   * Mirrors `CuratedCalibres.refreshRowFromDoc`: this listing inherited the
   * problem the moment it moved off Firestore.
   */
  const refreshRowFromDoc = useCallback(async (id: string) => {
    try {
      const full = await getMovementManufacturerById(id);
      if (!full) {
        setManufacturers((prev) => prev.filter((m) => m.id !== id));
        return;
      }
      setManufacturers((prev) =>
        prev.some((m) => m.id === id)
          ? prev.map((m) => (m.id === id ? full : m))
          : sortManufacturersByName([...prev, full]),
      );
    } catch (error) {
      // Non-fatal: the reload has already run, so the row is present — just
      // possibly one indexer-tick behind.
      console.warn('Could not refresh manufacturer row from Firestore', error);
    }
  }, []);

  /**
   * Apply values we KNOW were written, on top of a reload that may still be
   * serving pre-write index records. No reads — the outcome is already known.
   */
  const patchRows = useCallback(
    (ids: string[], patch: Partial<ManufacturerCatalogueEntry>) => {
      const idSet = new Set(ids);
      setManufacturers((prev) =>
        prev.map((m) => (idSet.has(m.id) ? { ...m, ...patch } : m)),
      );
    },
    [],
  );

  /** Same, for deletes: drop rows the index may not have dropped yet. */
  const pruneRows = useCallback((ids: string[]) => {
    const gone = new Set(ids);
    setManufacturers((prev) => prev.filter((m) => !gone.has(m.id)));
  }, []);

  const matchingManufacturers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matching = manufacturers.filter((m) => {
      if (!matchesParentOrgFilter(m, parentOrgFilter, parentOrgIndex)) return false;
      if (typeFilter === 'generic' && !m.isGeneric) return false;
      if (typeFilter === 'in-house' && m.isGeneric) return false;
      const includeLen = m.brandIdsManualInclude?.length ?? 0;
      if (includeFilter === 'empty' && includeLen > 0) return false;
      if (includeFilter === 'set' && includeLen === 0) return false;
      if (!term) return true;
      const hay = [
        m.name,
        m.displayName ?? '',
        m.id,
        m.country ?? '',
        m.parentOrg ?? '',
        // The resolved name too, so searching "Casio Computer" finds manufacturers
        // still stored under the legacy "Casio" spelling.
        m.parentOrg
          ? resolutionLabel(resolveParentOrgForManufacturer(m, parentOrgIndex)).label
          : '',
        ...(m.alternativeNames ?? []),
        ...(m.abbreviations ?? []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
    return matching;
  }, [manufacturers, searchTerm, typeFilter, includeFilter, parentOrgFilter, parentOrgIndex]);

  const parentOrgChipSet = useMemo(
    () => parentOrgChips(manufacturers, parentOrgIndex),
    [manufacturers, parentOrgIndex],
  );

  const needsCurationCount = useMemo(
    () =>
      manufacturers.filter(
        (m) => !m.isGeneric && (m.brandIdsManualInclude?.length ?? 0) === 0,
      ).length,
    [manufacturers],
  );

  const selectedList = useMemo(
    () => manufacturers.filter((m) => selected.has(m.id)),
    [manufacturers, selected],
  );

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Selection is order-independent, so it reads the unsorted matches — the
  // sorted list is built further down, once the Parent text can be resolved.
  const allFilteredSelected =
    matchingManufacturers.length > 0 && matchingManufacturers.every((m) => selected.has(m.id));
  const someFilteredSelected =
    matchingManufacturers.some((m) => selected.has(m.id)) && !allFilteredSelected;

  const toggleSelectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const m of matchingManufacturers) next.delete(m.id);
      } else {
        for (const m of matchingManufacturers) next.add(m.id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleSave = async (draft: MovementManufacturerDraft) => {
    setSaving(true);
    try {
      // Pull any external logo URL into our own Storage (mirrors the hero flow),
      // writing the canonical Storage URL to `logo` + provenance to `logoSourceUrl`.
      const manufacturerId =
        modalMode.kind === 'edit'
          ? modalMode.manufacturer.id
          : deriveManufacturerSlug(draft.name);
      const prevManufacturer =
        modalMode.kind === 'edit' ? modalMode.manufacturer : undefined;
      let resolvedLogo: { logo: string; logoSourceUrl: string };
      try {
        resolvedLogo = await resolveLogoToStorage({
          entity: 'manufacturers',
          id: manufacturerId,
          input: draft.logo ?? '',
          manualSourceUrl: draft.logoSourceUrl,
          prev: prevManufacturer
            ? { logo: prevManufacturer.logo, logoSourceUrl: prevManufacturer.logoSourceUrl }
            : undefined,
        });
      } catch (err) {
        if (err instanceof LogoFetchError) {
          resolvedLogo = { logo: '', logoSourceUrl: err.logoSourceUrl };
          toast.error('Could not fetch the logo image from its URL — saved the source URL only.');
        } else {
          throw err;
        }
      }
      // A pasted hero URL becomes our own Storage copy, as the logo does. A
      // fetch that fails keeps the URL the manufacturer had and says so.
      const resolvedHero = await resolveHeroToStorage({
        entity: 'manufacturers',
        id: manufacturerId,
        input: draft.heroImage ?? '',
        manualSourceUrl: draft.heroImageSourceUrl,
        prev: prevManufacturer
          ? { heroImage: prevManufacturer.heroImage, heroImageSourceUrl: prevManufacturer.heroImageSourceUrl }
          : undefined,
      });
      if (resolvedHero.fetchError) {
        toast.error(`Could not copy the hero image into Storage (${resolvedHero.fetchError}) — kept its URL.`);
      }
      const draftWithLogo: MovementManufacturerDraft = {
        ...draft,
        heroImage: resolvedHero.heroImage || (prevManufacturer?.heroImage ? '' : undefined),
        heroImageSourceUrl:
          resolvedHero.heroImageSourceUrl || (prevManufacturer?.heroImageSourceUrl ? '' : undefined),
        // Emit '' only to clear a previously-set value; undefined (→ stripped)
        // when there was never a logo, so untouched docs aren't polluted.
        logo: resolvedLogo.logo || (prevManufacturer?.logo ? '' : undefined),
        logoSourceUrl:
          resolvedLogo.logoSourceUrl || (prevManufacturer?.logoSourceUrl ? '' : undefined),
      };

      // After a create the form carries on in edit mode on the saved document,
      // so the parts that need its id (images, families, timeline…) can be
      // added without reopening it — the same hand-over as the brand form.
      let reopen: MovementManufacturer | null = null;
      if (modalMode.kind === 'create') {
        const createdId = await createMovementManufacturer(draftWithLogo);
        toast.success(`Created "${draft.name}" — images can be added now`);
        reopen = await getMovementManufacturerById(createdId).catch((err) => {
          console.error('Created, but could not reopen the manufacturer', err);
          return null;
        });
      } else if (modalMode.kind === 'edit') {
        // Clear a stored status when reverting to the default `active`
        // (draft.status is undefined for active; stripUndefined would otherwise
        // leave a stale `defunct`/`dormant` value behind).
        const statusClear =
          draftWithLogo.status === undefined && prevManufacturer?.status
            ? { status: deleteField() as unknown as undefined }
            : {};
        // Same convention for heritage: clear a stored list when emptied.
        const heritageClear =
          draftWithLogo.heritageBrandIds === undefined &&
          prevManufacturer?.heritageBrandIds?.length
            ? { heritageBrandIds: deleteField() as unknown as undefined }
            : {};
        // Year ceased: clear a stored value when emptied or when the status no
        // longer warrants one (the detail form drops yearCeased for active /
        // revived status).
        const yearCeasedClear =
          draftWithLogo.yearCeased === undefined &&
          prevManufacturer?.yearCeased !== undefined
            ? { yearCeased: deleteField() as unknown as undefined }
            : {};
        // Needs-review audit flag: clear a stored value when unchecked (draft
        // carries undefined for the false/unset case).
        const needsReviewClear =
          draftWithLogo.needsReview === undefined &&
          prevManufacturer?.needsReview
            ? { needsReview: deleteField() as unknown as undefined }
            : {};
        const supportsLineageClear =
          draftWithLogo.supportsLineage === undefined && prevManufacturer?.supportsLineage
            ? { supportsLineage: deleteField() as unknown as undefined }
            : {};
        // Revision terms: blank in the form ⇒ undefined ⇒ delete a stored value.
        const revisionTermsClear = {
          ...(draftWithLogo.revisionTermSingular === undefined && prevManufacturer?.revisionTermSingular
            ? { revisionTermSingular: deleteField() as unknown as undefined }
            : {}),
          ...(draftWithLogo.revisionTermPlural === undefined && prevManufacturer?.revisionTermPlural
            ? { revisionTermPlural: deleteField() as unknown as undefined }
            : {}),
        };
        // Docs links: same convention again — the detail form emits undefined
        // once every row has been removed, and stripUndefined would otherwise
        // drop the key and leave the old links in the document.
        const docsClear =
          draftWithLogo.docs === undefined && prevManufacturer?.docs?.length
            ? { docs: deleteField() as unknown as undefined }
            : {};
        // Parent org, both halves of the dual-write. Clearing the picker leaves
        // both undefined, and stripUndefined would drop them — so before this the
        // legacy string survived a Clear. Delete each one that was stored.
        const parentOrgClear =
          draftWithLogo.parentOrg === undefined && prevManufacturer?.parentOrg
            ? { parentOrg: deleteField() as unknown as undefined }
            : {};
        const parentOrgIdClear =
          draftWithLogo.parentOrgId === undefined && prevManufacturer?.parentOrgId
            ? { parentOrgId: deleteField() as unknown as undefined }
            : {};
        // Identity + location detail: same convention — the detail form emits
        // undefined when blank, and stripUndefined would otherwise drop the key
        // and leave the old value in the document.
        const cityClear =
          draftWithLogo.city === undefined && prevManufacturer?.city
            ? { city: deleteField() as unknown as undefined }
            : {};
        const stateRegionClear =
          draftWithLogo.stateRegion === undefined && prevManufacturer?.stateRegion
            ? { stateRegion: deleteField() as unknown as undefined }
            : {};
        const assemblyLocationClear =
          draftWithLogo.assemblyLocation === undefined && prevManufacturer?.assemblyLocation
            ? { assemblyLocation: deleteField() as unknown as undefined }
            : {};
        const instagramUrlClear =
          draftWithLogo.instagramUrl === undefined && prevManufacturer?.instagramUrl
            ? { instagramUrl: deleteField() as unknown as undefined }
            : {};
        const founderClear =
          draftWithLogo.founder === undefined && prevManufacturer?.founder
            ? { founder: deleteField() as unknown as undefined }
            : {};
        const ownershipTypeClear =
          draftWithLogo.ownershipType === undefined && prevManufacturer?.ownershipType
            ? { ownershipType: deleteField() as unknown as undefined }
            : {};
        const yearRevivedClear =
          draftWithLogo.yearRevived === undefined && prevManufacturer?.yearRevived !== undefined
            ? { yearRevived: deleteField() as unknown as undefined }
            : {};
        const predecessorManufacturerClear =
          draftWithLogo.predecessorManufacturer === undefined &&
          prevManufacturer?.predecessorManufacturer
            ? { predecessorManufacturer: deleteField() as unknown as undefined }
            : {};
        // Rich description: an emptied editor emits '' for each stored field
        // (description + descriptionRich travel together) — delete them.
        const descriptionClear = {
          ...(draftWithLogo.descriptionRich === ''
            ? { descriptionRich: deleteField() as unknown as undefined }
            : {}),
          ...(draftWithLogo.description === ''
            ? { description: deleteField() as unknown as undefined }
            : {}),
        };
        await updateMovementManufacturer(modalMode.manufacturer.id, {
          ...draftWithLogo,
          ...descriptionClear,
          ...statusClear,
          ...heritageClear,
          ...yearCeasedClear,
          ...needsReviewClear,
          ...supportsLineageClear,
          ...revisionTermsClear,
          ...docsClear,
          ...parentOrgClear,
          ...parentOrgIdClear,
          ...cityClear,
          ...stateRegionClear,
          ...assemblyLocationClear,
          ...instagramUrlClear,
          ...founderClear,
          ...ownershipTypeClear,
          ...yearRevivedClear,
          ...predecessorManufacturerClear,
        });
        toast.success(`Saved "${draft.name}"`);
      }
      invalidateManufacturerIndex();
      // The calibre screens resolve manufacturer docs through a module-level
      // cache, which the Algolia index refresh above knows nothing about.
      invalidateDocsLinks('manufacturer', manufacturerId);
      setModalMode(reopen ? { kind: 'edit', manufacturer: reopen } : { kind: 'closed' });
      await refresh();
      // The reload may still be serving the pre-save record for the row that was
      // just edited; rebuild that one row from its document.
      await refreshRowFromDoc(manufacturerId);
    } catch (err) {
      console.error('Save failed', err);
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Open the edit modal on a WHOLE document, not on the catalogue row behind it.
   *
   * One `getDoc` per Edit click, and the only Firestore read left on this tab.
   * Load-bearing rather than a nicety: the detail form seeds its draft from
   * every field of `initial` and Save writes that draft back wholesale, so
   * handing it a projection would clear `description`, `aiDescription`,
   * `needsReview`, `yearCeased`, `logoSourceUrl` and `heroImageSourceUrl` on the
   * first save of any manufacturer that had them. `handleSave` also diffs
   * against `modalMode.manufacturer` to decide which fields to `deleteField()`,
   * which needs the stored values to compare with.
   */
  const openEdit = useCallback(async (id: string) => {
    setOpeningId(id);
    try {
      const full = await getMovementManufacturerById(id);
      if (!full) {
        toast.error('That manufacturer no longer exists — refreshing.');
        await refresh();
        return;
      }
      setModalMode({ kind: 'edit', manufacturer: full });
    } catch (err) {
      console.error('Failed to load manufacturer for editing', err);
      toast.error('Could not open that manufacturer');
    } finally {
      setOpeningId(null);
    }
  }, [refresh]);

  // Deep-link from another tab. `openEdit` hydrates the document straight from
  // Firestore, so this does not wait for the listing's catalogue to load — and
  // it must not open from a catalogue row, which is an index projection.
  useEffect(() => {
    if (!openManufacturerId) return;
    onOpenManufacturerConsumed?.();
    void openEdit(openManufacturerId);
  }, [openManufacturerId, onOpenManufacturerConsumed, openEdit]);

  /**
   * A row-shaped optimistic patch, from the cross-manufacturer Data Health
   * overview's inline quick actions (promote / exclude / demote / set primary).
   *
   * Those only ever touch `brandIdsManualInclude`, `brandExclusions` and
   * `primaryBrandId`, all of which the projection carries — so the patch lands
   * on the list directly, and is SPREAD OVER the open modal's document rather
   * than replacing it. Replacing would swap a whole document for a projection
   * and re-open the six-field clearing hole; spreading keeps the unindexed
   * fields, because an entry omits absent keys instead of carrying them as
   * undefined.
   */
  const handleRowPatched = useCallback((updated: ManufacturerCatalogueEntry) => {
    setManufacturers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    setModalMode((mode) =>
      mode.kind === 'edit' && mode.manufacturer.id === updated.id
        ? { kind: 'edit', manufacturer: { ...mode.manufacturer, ...updated } }
        : mode,
    );
  }, []);

  /**
   * A document-shaped patch, from the per-manufacturer Data Health section
   * inside the open edit form. The modal takes it whole; the list takes it as
   * the projection it is a superset of.
   */
  const handleModalPatched = useCallback((updated: MovementManufacturer) => {
    setManufacturers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    setModalMode((mode) =>
      mode.kind === 'edit' && mode.manufacturer.id === updated.id
        ? { kind: 'edit', manufacturer: updated }
        : mode,
    );
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const deletedId = deleteTarget.id;
      await deleteMovementManufacturer(deletedId);
      toast.success(`Deleted "${deleteTarget.name}"`);
      invalidateManufacturerIndex();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
      setDeleteTarget(null);
      await refresh();
      // The index may not have dropped the record yet — a deleted row reappearing
      // reads as a failed delete.
      pruneRows([deletedId]);
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSetGeneric = async (isGeneric: boolean) => {
    if (selectedList.length === 0) return;
    setBulkBusy(true);
    try {
      const ids = selectedList.map((m) => m.id);
      await bulkSetManufacturerGeneric(ids, isGeneric);
      toast.success(
        `Marked ${selectedList.length} as ${isGeneric ? 'Generic' : 'In-house'}`,
      );
      await refresh();
      // Re-assert what we know landed: re-showing the old Type badge on a row the
      // admin just flipped reads as a failed write.
      patchRows(ids, { isGeneric });
    } catch (err) {
      console.error('Bulk update failed', err);
      toast.error('Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const openMerge = () => {
    if (selectedList.length < 2) return;
    setMergeCanonicalId(selectedList[0].id);
    setMergeOpen(true);
  };

  const closeMerge = () => {
    if (mergeRunning) return;
    setMergeOpen(false);
    setMergeCanonicalId(null);
  };

  const handleMerge = async () => {
    if (!mergeCanonicalId) return;
    // Captured before the state resets below, so the post-refresh top-up still
    // knows which row survived the merge.
    const canonicalId = mergeCanonicalId;
    const mergedIds = selectedList
      .map((m) => m.id)
      .filter((id) => id !== canonicalId);
    if (mergedIds.length === 0) return;
    setMergeRunning(true);
    try {
      const result = await callMergeMovementManufacturers(canonicalId, mergedIds);
      toast.success(
        `Merged ${result.entitiesDeleted} into ${result.canonicalId}: ${result.calibresUpdated} calibre${
          result.calibresUpdated === 1 ? '' : 's'
        } re-stamped, +${result.alternativeNamesAdded} alt name${
          result.alternativeNamesAdded === 1 ? '' : 's'
        }`,
      );
      clearSelection();
      setMergeOpen(false);
      setMergeCanonicalId(null);
      await refresh();
      // Merged entities are deleted and the canonical gains their alt names —
      // both lag the index.
      pruneRows(mergedIds);
      await refreshRowFromDoc(canonicalId);
    } catch (err) {
      console.error('Merge failed', err);
      toast.error(err instanceof Error ? err.message : 'Merge failed');
    } finally {
      setMergeRunning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedList.length === 0) return;
    setBulkDeleteRunning(true);
    try {
      const ids = selectedList.map((m) => m.id);
      const result = await callDeleteMovementManufacturers(ids);
      toast.success(
        `Deleted ${result.entitiesDeleted} manufacturer${
          result.entitiesDeleted === 1 ? '' : 's'
        }, cleared ${result.calibresCleared} calibre FK${
          result.calibresCleared === 1 ? '' : 's'
        }`,
      );
      clearSelection();
      setBulkDeleteOpen(false);
      await refresh();
      // Same as the single delete: the index may still be serving the records.
      pruneRows(ids);
    } catch (err) {
      console.error('Bulk delete failed', err);
      toast.error(err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setBulkDeleteRunning(false);
    }
  };

  const handleResyncToAlgolia = async () => {
    const includedKeys = RESYNC_INDEXES.map((r) => r.key).filter((k) => resyncInclude[k]);
    if (includedKeys.length === 0) return;
    setResyncRunning(true);
    setResyncSummary(null);
    const lines: string[] = [];
    try {
      // Run sequentially so each index's progress is independent and a failure
      // surfaces which index it happened on.
      for (const key of includedKeys) {
        const full = resyncFull[key];
        const label = RESYNC_INDEXES.find((x) => x.key === key)!.label;
        if (key === 'references') {
          const r = await callResyncAllReferencesToAlgolia({ fullRebuild: full });
          lines.push(
            full
              ? `${label}: ${r.resynced.toLocaleString()} reindexed (full rebuild)`
              : `${label}: ${r.resynced.toLocaleString()} synced, ${r.orphansRemoved} orphan${r.orphansRemoved === 1 ? '' : 's'} removed`,
          );
        } else {
          const fn =
            key === 'brands'
              ? callBackfillBrandsToAlgolia
              : key === 'manufacturers'
                ? callBackfillManufacturersToAlgolia
                : callBackfillCalibresToAlgolia;
          const r = await fn({ fullRebuild: full });
          lines.push(
            `${label}: ${r.indexed.toLocaleString()}/${r.scanned.toLocaleString()} indexed${full ? ' (full rebuild)' : ''}`,
          );
        }
      }
      setResyncSummary(lines);
      toast.success(lines.join(' · '));
    } catch (err) {
      console.error('Resync to Algolia failed', err);
      setResyncSummary(lines.length > 0 ? [...lines, 'Stopped on error.'] : null);
      toast.error(err instanceof Error ? err.message : 'Resync failed');
    } finally {
      setResyncRunning(false);
    }
  };

  // Entity index settings push / backfill. Idempotent and safe; runs without a
  // confirm dialog and reports via toast. `key` doubles as the busy marker.
  const runEntityIndexAction = async (
    key: string,
    label: string,
    fn: () => Promise<EntityIndexBackfillResult | EntityIndexSettingsResult>,
  ) => {
    if (entityIndexBusy) return;
    setEntityIndexBusy(key);
    try {
      const r = await fn();
      if ('indexed' in r) {
        toast.success(
          `${label}: ${r.scanned.toLocaleString()} scanned, ${r.indexed.toLocaleString()} indexed → ${r.index}`,
        );
      } else {
        toast.success(`${label}: settings pushed → ${r.index}`);
      }
    } catch (err) {
      console.error(`${label} failed`, err);
      toast.error(err instanceof Error ? err.message : `${label} failed`);
    } finally {
      setEntityIndexBusy(null);
    }
  };

  const idToManufacturer = useMemo(() => {
    const m = new Map<string, ManufacturerCatalogueEntry>();
    for (const x of manufacturers) m.set(x.id, x);
    return m;
  }, [manufacturers]);

  /**
   * What the Parent column says about a manufacturer: the parent manufacturer's
   * name when there is one, otherwise the resolved parent organization. ONE
   * function feeds both the cell and the Parent sort, so the list can never be
   * ordered by something other than what it shows.
   */
  const parentInfo = useCallback(
    (m: ManufacturerCatalogueEntry): { text: string; matched: boolean; title?: string } => {
      if (m.parentManufacturerId) {
        const parent = idToManufacturer.get(m.parentManufacturerId);
        return {
          text: parent ? (parent.displayName || parent.name) : m.parentManufacturerId,
          matched: true,
        };
      }
      const resolution = resolveParentOrgForManufacturer(m, parentOrgIndex);
      if (resolution.kind !== 'none') {
        const { label, matched, title } = resolutionLabel(resolution);
        return { text: label, matched, title };
      }
      return { text: '', matched: true };
    },
    [idToManufacturer, parentOrgIndex],
  );

  const parentLabel = (m: ManufacturerCatalogueEntry) => {
    const { text, matched, title } = parentInfo(m);
    if (!text) return '—';
    if (!title && matched) return text;
    return (
      <span className={matched ? undefined : 'font-medium text-amber-700'} title={title}>
        {text}
      </span>
    );
  };

  const filtered = useMemo(
    () =>
      sortManufacturers(matchingManufacturers, sortOrder, {
        parentText: (m) => parentInfo(m).text,
        calibreCount: (m) => calibreCounts.get(m.id) ?? 0,
      }),
    [matchingManufacturers, sortOrder, parentInfo, calibreCounts],
  );

  const sortHeader = (field: ManufacturerSortField, label: string, align: 'left' | 'right' = 'left') => (
    <th
      className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700`}
      onClick={() => setSortOrder((current) => nextManufacturerSort(current, field))}
      aria-sort={
        sortOrder.field === field
          ? sortOrder.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      {label} <SortIcon active={sortOrder.field === field} dir={sortOrder.direction} />
    </th>
  );

  const parentOptionsFor = (currentId: string | undefined) =>
    manufacturers.filter((m) => m.id !== currentId);

  // Unified resync gating: require RESYNC normally, escalate to FULL REBUILD
  // when any included index is set to wipe-first.
  const resyncAnyIncluded = RESYNC_INDEXES.some((r) => resyncInclude[r.key]);
  const resyncAnyFull = RESYNC_INDEXES.some((r) => resyncInclude[r.key] && resyncFull[r.key]);
  const resyncRequiredWord = resyncAnyFull ? 'FULL REBUILD' : 'RESYNC';

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Movement Manufacturers</h2>
          <p className="text-sm text-gray-500 mt-1">
            First-class entity backing each calibre&apos;s manufacturer FK. {manufacturers.length} total.
          </p>
        </div>
        <div className="shrink-0">
          <div className="flex items-start gap-4 flex-wrap justify-end">
            {(showCalibreMissingManufacturerIdAudit ||
              showRefsUnresolvedCalibreAudit ||
              showCalibresUnresolvedBrandIdAudit ||
              showAkaBrandIdShapeAudit ||
              showManufacturerDataHealth) && (
              <div className="flex flex-col items-stretch gap-1">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                  Diagnostics
                </div>
                <div className="flex flex-col items-stretch gap-2">
                  {showCalibreMissingManufacturerIdAudit && (
                    <Button variant="secondary" onClick={() => setAuditOpen(true)}>
                      Audit Missing Manufacturer IDs
                    </Button>
                  )}
                  {showRefsUnresolvedCalibreAudit && (
                    <Button variant="secondary" onClick={() => setRefsAuditOpen(true)}>
                      Audit Refs with Unresolved Calibre
                    </Button>
                  )}
                  {showCalibresUnresolvedBrandIdAudit && (
                    <Button variant="secondary" onClick={() => setCalibresUnresolvedBrandIdAuditOpen(true)}>
                      Audit Calibres with Unresolved brandId
                    </Button>
                  )}
                  {showAkaBrandIdShapeAudit && (
                    <Button variant="secondary" onClick={() => setAkaBrandIdAuditOpen(true)}>
                      Audit AKA brandId Shape
                    </Button>
                  )}
                  {showManufacturerDataHealth && (
                    <Button variant="secondary" onClick={() => setDataHealthOpen(true)}>
                      Data Health Overview
                    </Button>
                  )}
                </div>
              </div>
            )}
            {(showPushAlgoliaSettings || showResyncToAlgolia || showEntityIndexTools) && (
              <div className="flex flex-col items-stretch gap-1">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                  Maintenance
                </div>
                <div className="flex flex-col items-stretch gap-2">
                  {showPushAlgoliaSettings && (
                    <Button variant="secondary" onClick={() => setPushAlgoliaSettingsOpen(true)}>
                      Push Algolia Index Settings
                    </Button>
                  )}
                  {showResyncToAlgolia && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setResyncConfirmText('');
                        setResyncSummary(null);
                        setResyncInclude({
                          references: true,
                          brands: true,
                          manufacturers: true,
                          calibres: true,
                        });
                        setResyncFull({
                          references: false,
                          brands: false,
                          manufacturers: false,
                          calibres: false,
                        });
                        setResyncOpen(true);
                      }}
                    >
                      Resync to Algolia
                    </Button>
                  )}
                  {showEntityIndexTools && (
                    <>
                      <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mt-1">
                        Entity index settings
                      </div>
                      <Button
                        variant="secondary"
                        disabled={!!entityIndexBusy}
                        onClick={() =>
                          runEntityIndexAction('push-brands', 'Brand index settings', callPushAlgoliaBrandIndexSettings)
                        }
                      >
                        {entityIndexBusy === 'push-brands' ? 'Pushing…' : 'Push Brand Index Settings'}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={!!entityIndexBusy}
                        onClick={() =>
                          runEntityIndexAction('push-mfrs', 'Manufacturer index settings', callPushAlgoliaManufacturerIndexSettings)
                        }
                      >
                        {entityIndexBusy === 'push-mfrs' ? 'Pushing…' : 'Push Manufacturer Index Settings'}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={!!entityIndexBusy}
                        onClick={() =>
                          runEntityIndexAction('push-calibres', 'Calibre index settings', callPushAlgoliaCalibreIndexSettings)
                        }
                      >
                        {entityIndexBusy === 'push-calibres' ? 'Pushing…' : 'Push Calibre Index Settings'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-col items-start gap-1">
              <div className="text-[11px] uppercase tracking-wide text-transparent font-medium select-none">
                Add
              </div>
              <Button onClick={() => setModalMode({ kind: 'create' })}>Add Manufacturer</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk-action toolbar */}
      {selectedList.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">
          <span className="text-sm text-brand-800">
            {selectedList.length} selected
          </span>
          <Button
            size="sm"
            onClick={openMerge}
            disabled={selectedList.length < 2 || bulkBusy}
            title={selectedList.length < 2 ? 'Select 2 or more to merge' : ''}
          >
            Merge selected
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleBulkSetGeneric(true)}
            loading={bulkBusy}
          >
            Mark as Generic
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleBulkSetGeneric(false)}
            loading={bulkBusy}
          >
            Mark as In-House
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setBulkDeleteOpen(true)}
            disabled={bulkBusy}
          >
            Delete selected ({selectedList.length})
          </Button>
          <button
            type="button"
            onClick={clearSelection}
            disabled={bulkBusy}
            className="text-sm text-gray-600 hover:text-gray-800 ml-auto"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="mb-4 space-y-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, display name, slug, country, alt name…"
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">Type:</span>
            {(['all', 'generic', 'in-house'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setTypeFilter(opt)}
                className={`px-2 py-1 rounded-full text-xs border ${
                  typeFilter === opt
                    ? 'bg-brand-100 border-brand-300 text-brand-800'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt === 'all' ? 'All' : opt === 'generic' ? 'Generic' : 'In-house'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">
              Brand-include:
            </span>
            {(['all', 'empty', 'set'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setIncludeFilter(opt)}
                className={`px-2 py-1 rounded-full text-xs border ${
                  includeFilter === opt
                    ? 'bg-brand-100 border-brand-300 text-brand-800'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt === 'all' ? 'All' : opt === 'empty' ? 'Empty' : 'Set'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setTypeFilter('in-house');
              setIncludeFilter('empty');
            }}
            className="ml-auto text-xs px-2 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            title="In-house manufacturers with no brand include list set"
          >
            Needs curation ({needsCurationCount})
          </button>
        </div>
        {(parentOrgChipSet.chips.length > 0 || parentOrgChipSet.unset > 0) && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">Parent org:</span>
            {parentOrgChipSet.chips.map((chip) => {
              const active = parentOrgFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  title={chip.title}
                  aria-pressed={active}
                  onClick={() => setParentOrgFilter(active ? null : chip.key)}
                  className={`px-2 py-1 rounded-full text-xs border ${
                    active
                      ? 'bg-brand-100 border-brand-300 text-brand-800'
                      : chip.matched
                        ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                  }`}
                >
                  🏢 {chip.label} ({chip.count})
                </button>
              );
            })}
            {parentOrgChipSet.unset > 0 && (
              <button
                type="button"
                aria-pressed={parentOrgFilter === UNSET_PARENT_ORG_FILTER}
                onClick={() =>
                  setParentOrgFilter(
                    parentOrgFilter === UNSET_PARENT_ORG_FILTER ? null : UNSET_PARENT_ORG_FILTER,
                  )
                }
                className={`px-2 py-1 rounded-full text-xs border ${
                  parentOrgFilter === UNSET_PARENT_ORG_FILTER
                    ? 'bg-brand-100 border-brand-300 text-brand-800'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                ❓ Unset ({parentOrgChipSet.unset})
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someFilteredSelected;
                      }}
                      onChange={toggleSelectAllFiltered}
                      aria-label="Select all"
                      className="rounded text-brand-600 focus:ring-brand-500"
                    />
                  </th>
                  {sortHeader('name', 'Name')}
                  {sortHeader('country', 'Country')}
                  {sortHeader('parent', 'Parent')}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  {sortHeader('calibres', 'Calibres', 'right')}
                  {sortHeader('created', 'Created')}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((m) => {
                  const isSelected = selected.has(m.id);
                  const calibreCount = calibreCounts.get(m.id) ?? 0;
                  return (
                    <tr key={m.id} className={isSelected ? 'bg-brand-50/40' : undefined}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(m.id)}
                          aria-label={`Select ${m.name}`}
                          className="rounded text-brand-600 focus:ring-brand-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{m.displayName ?? m.name}</span>
                          <StatusBadge status={m.status} />
                          {(m.heritageBrandIds?.length ?? 0) > 0 && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700"
                              title={`Heritage: traces lineage from ${m.heritageBrandIds!.length} manufacturer(s)`}
                            >
                              ⚘ Heritage
                            </span>
                          )}
                        </div>
                        {m.displayName && m.displayName !== m.name && (
                          <div className="text-xs text-gray-500">{m.name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{m.country ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {parentLabel(m)}
                      </td>
                      <td className="px-4 py-3">
                        {m.isGeneric ? (
                          <Badge variant="default">Generic</Badge>
                        ) : (
                          <Badge variant="success">In-house</Badge>
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-sm text-right tabular-nums ${
                          calibreCount === 0 ? 'text-gray-400' : 'text-gray-900'
                        }`}
                        title={
                          calibreCount === 0
                            ? 'No calibres reference this manufacturer (orphan)'
                            : `${calibreCount} calibre${calibreCount === 1 ? '' : 's'}`
                        }
                      >
                        {calibreCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {m.createdAt ? (
                          m.createdAt.toDate().toLocaleDateString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void openEdit(m.id)}
                            loading={openingId === m.id}
                            disabled={openingId !== null}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(m)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {searchTerm
                ? `No manufacturers match "${searchTerm}"`
                : 'No manufacturers yet. Click "Add Manufacturer" or run "Migrate from lookup".'}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit modal */}
      <Modal
        isOpen={modalMode.kind !== 'closed'}
        onClose={() => (saving ? undefined : setModalMode({ kind: 'closed' }))}
        title={
          modalMode.kind === 'edit' ? `Edit ${modalMode.manufacturer.name}` : 'Add Manufacturer'
        }
        size="full"
        headerActions={
          <CopyLinkButton
            url={
              modalMode.kind === 'edit'
                ? buildShareUrl('manufacturer', modalMode.manufacturer.id)
                : null
            }
            noun="manufacturer"
          />
        }
      >
        {modalMode.kind !== 'closed' && (
          <DetailWrapper
            // Remount on create → edit, so the form seeds from the saved document.
            key={modalMode.kind === 'edit' ? modalMode.manufacturer.id : 'create'}
            mode={modalMode}
            parentOptions={parentOptionsFor(
              modalMode.kind === 'edit' ? modalMode.manufacturer.id : undefined,
            )}
            onCancel={() => setModalMode({ kind: 'closed' })}
            onSave={handleSave}
            saving={saving}
            onManufacturerChange={handleModalPatched}
            parentOrgIndex={parentOrgIndex}
            parentOrgsLoading={parentOrgsLoading}
            parentOrgsError={parentOrgsError}
          />
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => (saving ? undefined : setDeleteTarget(null))}
        title="Delete manufacturer"
        size="sm"
      >
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-gray-700">
              Delete <strong>{deleteTarget.name}</strong> ({deleteTarget.id})? Calibres that
              reference this manufacturerId will keep their string but lose the FK.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={saving}>
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Merge modal */}
      <Modal
        isOpen={mergeOpen}
        onClose={closeMerge}
        title="Merge manufacturers"
        size="md"
      >
        {mergeOpen && (
          <MergePanel
            selected={selectedList}
            calibreCounts={calibreCounts}
            canonicalId={mergeCanonicalId}
            onCanonicalChange={setMergeCanonicalId}
            onCancel={closeMerge}
            onConfirm={handleMerge}
            running={mergeRunning}
          />
        )}
      </Modal>

      {/* Bulk-delete confirm */}
      <Modal
        isOpen={bulkDeleteOpen}
        onClose={() => (bulkDeleteRunning ? undefined : setBulkDeleteOpen(false))}
        title="Delete selected manufacturers"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Delete <strong>{selectedList.length}</strong> selected manufacturer
            {selectedList.length === 1 ? '' : 's'}? Calibres currently pointing at them
            will have their <code className="font-mono">manufacturerId</code> cleared (the
            original <code className="font-mono">manufacturer</code> string is preserved).
            The entity docs will be deleted.
          </p>
          {selectedList.length > 0 && (
            <ul className="text-xs text-gray-500 max-h-40 overflow-auto bg-gray-50 border rounded p-2 space-y-0.5">
              {selectedList.map((m) => {
                const n = calibreCounts.get(m.id) ?? 0;
                return (
                  <li key={m.id}>
                    <span className="font-mono">{m.id}</span> — {m.displayName ?? m.name}
                    {n > 0 && (
                      <span className="text-amber-700"> ({n} calibre{n === 1 ? '' : 's'} will be unlinked)</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={bulkDeleteRunning}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleBulkDelete}
              loading={bulkDeleteRunning}
            >
              Delete {selectedList.length}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Push Algolia index settings */}
      <Modal
        isOpen={pushAlgoliaSettingsOpen}
        onClose={() => setPushAlgoliaSettingsOpen(false)}
        title="Push Algolia index settings"
        size="lg"
      >
        <PushAlgoliaSettingsPanel
          onClosed={() => setPushAlgoliaSettingsOpen(false)}
        />
      </Modal>

      {/* Unified Resync to Algolia */}
      <Modal
        isOpen={resyncOpen}
        onClose={() => (resyncRunning ? undefined : setResyncOpen(false))}
        title="Resync to Algolia"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            Re-push the selected indexes to Algolia. <strong>Incremental</strong> upserts
            every record with no downtime. <strong>Full rebuild</strong> wipes the index via
            an atomic replace before reindexing — use only when records are structurally out
            of sync. References stream from{' '}
            <code className="font-mono">watchBrands/*/references/*</code>; brands, manufacturers
            and calibres stream from their own collections.
          </div>
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {RESYNC_INDEXES.map((row) => (
              <div key={row.key} className="flex items-center justify-between gap-3 px-3 py-2">
                <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resyncInclude[row.key]}
                    disabled={resyncRunning}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setResyncInclude((prev) => ({ ...prev, [row.key]: checked }));
                      setResyncConfirmText('');
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="font-medium">{row.label}</span>
                </label>
                <label
                  className={`flex items-center gap-2 text-xs cursor-pointer ${
                    resyncInclude[row.key] ? 'text-red-700' : 'text-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={resyncFull[row.key]}
                    disabled={resyncRunning || !resyncInclude[row.key]}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setResyncFull((prev) => ({ ...prev, [row.key]: checked }));
                      setResyncConfirmText('');
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Full rebuild (wipe index first)
                </label>
              </div>
            ))}
          </div>
          {resyncAnyFull && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900">
              <strong>Full rebuild selected.</strong> The chosen index(es) are wiped via an
              atomic replace and reindexed from scratch.
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <code className="font-mono">{resyncRequiredWord}</code> to confirm:
            </label>
            <input
              type="text"
              value={resyncConfirmText}
              onChange={(e) => setResyncConfirmText(e.target.value)}
              autoFocus
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 font-mono ${
                resyncAnyFull
                  ? 'focus:ring-2 focus:ring-red-500 focus:border-red-500'
                  : 'focus:ring-2 focus:ring-amber-500 focus:border-amber-500'
              }`}
            />
          </div>
          {resyncSummary && (
            <ul className="border-t pt-3 space-y-1 text-sm text-gray-700 list-disc ml-5">
              {resyncSummary.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setResyncOpen(false)}
              disabled={resyncRunning}
            >
              Close
            </Button>
            <Button
              variant={resyncAnyFull ? 'danger' : 'primary'}
              onClick={handleResyncToAlgolia}
              loading={resyncRunning}
              disabled={
                resyncRunning ||
                !resyncAnyIncluded ||
                resyncConfirmText.trim() !== resyncRequiredWord
              }
            >
              {resyncAnyFull ? 'Full rebuild' : 'Resync'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Audit calibres missing manufacturerId */}
      <Modal
        isOpen={auditOpen}
        onClose={() => setAuditOpen(false)}
        title="Audit calibres missing manufacturerId"
        size="lg"
      >
        <AuditMissingManufacturerIdPanel onClosed={() => setAuditOpen(false)} />
      </Modal>

      {/* Audit refs with unresolved calibre */}
      <Modal
        isOpen={refsAuditOpen}
        onClose={() => setRefsAuditOpen(false)}
        title="Audit refs with unresolved calibre"
        size="xl"
      >
        <AuditRefsUnresolvedCalibrePanel onClosed={() => setRefsAuditOpen(false)} />
      </Modal>

      {/* Audit calibres with unresolved brandId */}
      <Modal
        isOpen={calibresUnresolvedBrandIdAuditOpen}
        onClose={() => setCalibresUnresolvedBrandIdAuditOpen(false)}
        title="Audit calibres with unresolved brandId"
        size="lg"
      >
        <AuditCalibresUnresolvedBrandIdPanel
          onClosed={() => setCalibresUnresolvedBrandIdAuditOpen(false)}
        />
      </Modal>

      {/* Audit AKA (aliasDetails) brandId shape */}
      <Modal
        isOpen={akaBrandIdAuditOpen}
        onClose={() => setAkaBrandIdAuditOpen(false)}
        title="Audit AKA brandId shape"
        size="xl"
      >
        <AuditAkaBrandIdShapePanel onClosed={() => setAkaBrandIdAuditOpen(false)} />
      </Modal>

      {/* Cross-manufacturer Data Health overview (read-only, Phase 2) */}
      <Modal
        isOpen={dataHealthOpen}
        onClose={() => setDataHealthOpen(false)}
        title="Manufacturer Data Health"
        size="full"
      >
        <ManufacturerDataHealthOverview
          manufacturers={manufacturers}
          onManufacturerChange={handleRowPatched}
          onOpenManufacturer={(m) => {
            // Leave the overview open behind — the edit modal stacks on top
            // (Modal refcounts z-index / Escape), so closing it returns here and
            // the user can keep iterating through manufacturers.
            void openEdit(m.id);
          }}
        />
      </Modal>

    </div>
  );
}

function MergePanel({
  selected,
  calibreCounts,
  canonicalId,
  onCanonicalChange,
  onCancel,
  onConfirm,
  running,
}: {
  selected: ManufacturerCatalogueEntry[];
  calibreCounts: Map<string, number>;
  canonicalId: string | null;
  onCanonicalChange: (id: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  running: boolean;
}) {
  const merged = selected.filter((m) => m.id !== canonicalId);
  const canonical = selected.find((m) => m.id === canonicalId);

  // Best-effort preview of how many new alt names will be added.
  // Mirrors the server-side dedup logic.
  const altNamesPreview = useMemo(() => {
    if (!canonical) return 0;
    const seen = new Set<string>();
    for (const n of canonical.alternativeNames ?? []) {
      const k = n.trim().toLowerCase();
      if (k) seen.add(k);
    }
    seen.add(canonical.name.trim().toLowerCase());
    let count = 0;
    for (const md of merged) {
      const candidates = [md.name, ...(md.alternativeNames ?? [])];
      for (const c of candidates) {
        const k = c.trim().toLowerCase();
        if (!k || seen.has(k)) continue;
        seen.add(k);
        count += 1;
      }
    }
    return count;
  }, [canonical, merged]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Pick which manufacturer keeps its entity. The others will be deleted; every calibre
        currently pointing at them gets re-stamped to the canonical entity, and their{' '}
        <em>name</em> + <em>alternativeNames</em> are appended to the canonical&apos;s{' '}
        <em>alternativeNames</em> (deduped). Other fields on the merged entities are{' '}
        <strong>discarded</strong>.
      </p>

      <div>
        <div className="text-sm font-medium text-gray-700 mb-2">Canonical:</div>
        <div className="border rounded-lg divide-y">
          {selected.map((m) => {
            const n = calibreCounts.get(m.id) ?? 0;
            return (
              <label
                key={m.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${
                  m.id === canonicalId ? 'bg-brand-50' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="merge-canonical"
                  checked={m.id === canonicalId}
                  onChange={() => onCanonicalChange(m.id)}
                  disabled={running}
                  className="mt-1 text-brand-600 focus:ring-brand-500"
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium">
                    {m.displayName ?? m.name}{' '}
                    <span
                      className={`font-normal tabular-nums ${
                        n === 0 ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      ({n} calibre{n === 1 ? '' : 's'})
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">{m.id}</div>
                  {m.alternativeNames && m.alternativeNames.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {m.alternativeNames.length} alt name
                      {m.alternativeNames.length === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1">
        {canonical ? (
          <>
            <div>
              <strong>{merged.length}</strong> entit
              {merged.length === 1 ? 'y' : 'ies'} will be deleted.
            </div>
            <div>
              Calibres currently pointing at the merged entities will be re-stamped to{' '}
              <code className="font-mono">{canonical.id}</code>.
            </div>
            <div>
              Approx <strong>{altNamesPreview}</strong> new alternative name
              {altNamesPreview === 1 ? '' : 's'} will be appended to the canonical.
            </div>
          </>
        ) : (
          <div>Pick a canonical.</div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t">
        <Button variant="secondary" onClick={onCancel} disabled={running}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          loading={running}
          disabled={!canonical || merged.length === 0}
        >
          Merge {merged.length} into {canonical?.id ?? '…'}
        </Button>
      </div>
    </div>
  );
}

function DetailWrapper({
  mode,
  parentOptions,
  onCancel,
  onSave,
  saving,
  onManufacturerChange,
  parentOrgIndex,
  parentOrgsLoading,
  parentOrgsError,
}: {
  mode: Exclude<ModalMode, { kind: 'closed' }>;
  parentOptions: ManufacturerCatalogueEntry[];
  onCancel: () => void;
  onSave: (draft: MovementManufacturerDraft) => Promise<void>;
  saving: boolean;
  onManufacturerChange: (updated: MovementManufacturer) => void;
  parentOrgIndex: ParentOrgIndex;
  parentOrgsLoading: boolean;
  parentOrgsError: boolean;
}) {
  const initial = mode.kind === 'edit' ? mode.manufacturer : null;
  const existingSlug = mode.kind === 'edit' ? mode.manufacturer.id : undefined;

  return (
    <MovementManufacturerDetail
      initial={initial}
      existingSlug={existingSlug}
      isNew={mode.kind === 'create'}
      parentOptions={parentOptions}
      onCancel={onCancel}
      onSave={onSave}
      saving={saving}
      onManufacturerChange={onManufacturerChange}
      parentOrgIndex={parentOrgIndex}
      parentOrgsLoading={parentOrgsLoading}
      parentOrgsError={parentOrgsError}
    />
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'brand' | 'gray' | 'warn';
}) {
  const toneClass =
    tone === 'brand'
      ? 'bg-brand-50 text-brand-700 border-brand-200'
      : tone === 'warn'
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-gray-50 text-gray-700 border-gray-200';
  return (
    <div className={`border rounded-lg p-3 ${toneClass}`}>
      <div className="text-xs">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function AuditMissingManufacturerIdPanel({ onClosed }: { onClosed: () => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CalibreManufacturerIdAuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runAudit = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await callAuditCalibresMissingManufacturerId();
      setResult(r);
    } catch (err) {
      console.error('Audit failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Read-only scan of <code className="font-mono">custom_calibres</code>. Tallies
        the <code className="font-mono">manufacturer</code> string on every calibre
        that has no <code className="font-mono">manufacturerId</code> stamped — use the
        punch list to decide which entities/synonyms to add before re-running the
        Phase 3 backfill. Writes nothing.
      </p>

      <div className="flex gap-2">
        <Button onClick={runAudit} loading={running} disabled={running}>
          Run Audit
        </Button>
        <div className="ml-auto">
          <Button variant="secondary" onClick={onClosed} disabled={running}>
            Close
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-medium mb-1">Audit call failed</div>
            <pre className="font-mono text-xs whitespace-pre-wrap break-words text-red-900">
              {error}
            </pre>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-red-700 hover:text-red-900 underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {result && (
        <div className="space-y-3 border-t pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Calibres scanned" value={result.calibresScanned} tone="gray" />
            <Stat
              label="Missing manufacturerId"
              value={result.calibresMissingManufacturerId}
              tone={result.calibresMissingManufacturerId > 0 ? 'warn' : 'brand'}
            />
            <Stat
              label="With string, no ID"
              value={result.calibresWithManufacturerStringButNoId}
              tone="gray"
            />
            <Stat
              label="No string either"
              value={result.calibresWithoutManufacturerString}
              tone="gray"
            />
          </div>

          {result.topManufacturerStrings.length > 0 ? (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                Top manufacturer strings on calibres missing a FK
                {result.topManufacturerStrings.length >= 100 ? ' (top 100)' : ''}
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Manufacturer string
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">
                          Count
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">
                          {' '}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {result.topManufacturerStrings.map((row) => (
                        <tr key={row.value.toLowerCase()}>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 break-all">
                            {row.value}
                          </td>
                          <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-700">
                            {row.count}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => copyValue(row.value)}
                              className="text-xs text-brand-600 hover:text-brand-800 underline"
                            >
                              Copy
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              No manufacturer strings to report. All calibres missing{' '}
              <code className="font-mono">manufacturerId</code> also lack a{' '}
              <code className="font-mono">manufacturer</code> string.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AuditRefsUnresolvedCalibrePanel({ onClosed }: { onClosed: () => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RefsUnresolvedCalibreAuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Default landing view is the pivot so the user sees where to focus first.
  const [view, setView] = useState<'pivot' | 'detail'>('pivot');
  const [brandFilter, setBrandFilter] = useState<string>(''); // '' = all brands
  // Rows the user has resolved this session. Keyed by `${brandId}::${calibreLower}`
  // so the same calibre string under different brands tracks independently.
  const [actionedKeys, setActionedKeys] = useState<Set<string>>(new Set());

  const runAudit = useCallback(async () => {
    setRunning(true);
    setError(null);
    setActionedKeys(new Set());
    try {
      const r = await callAuditRefsUnresolvedCalibre();
      setResult(r);
      setView('pivot');
      setBrandFilter('');
    } catch (err) {
      console.error('Audit failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Audit failed');
    } finally {
      setRunning(false);
    }
  }, []);

  const markActioned = useCallback((brandId: string, value: string) => {
    setActionedKeys((prev) => {
      const next = new Set(prev);
      next.add(`${brandId}::${value.toLowerCase()}`);
      return next;
    });
  }, []);

  const byBrandSorted = useMemo(() => {
    if (!result) return [];
    return Object.entries(result.byBrand)
      .map(([brandId, b]) => ({ brandId, ...b }))
      .sort((a, b) => b.totalUnresolved - a.totalUnresolved
        || a.brandDisplayName.localeCompare(b.brandDisplayName));
  }, [result]);

  const filteredRows = useMemo(() => {
    if (!result) return [];
    if (!brandFilter) return result.rows;
    return result.rows.filter((r) => r.brandId === brandFilter);
  }, [result, brandFilter]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Read-only scan of every <code className="font-mono">references</code>{' '}
        document, grouped by <strong>(brand, calibre string)</strong>. Same
        calibre resolution as <code className="font-mono">refsSkippedNoManufacturerId</code>{' '}
        from the brand-manufacturer backfill dry-run. Writes nothing.
      </p>

      <div className="flex gap-2 items-center flex-wrap">
        <Button onClick={runAudit} loading={running} disabled={running}>
          {result ? 'Re-run Audit' : 'Run Audit'}
        </Button>
        {result && (
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setView('pivot')}
              className={`px-3 py-1.5 ${view === 'pivot' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              By brand
            </button>
            <button
              type="button"
              onClick={() => setView('detail')}
              className={`px-3 py-1.5 border-l border-gray-300 ${view === 'detail' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Detail
            </button>
          </div>
        )}
        <div className="ml-auto">
          <Button variant="secondary" onClick={onClosed} disabled={running}>
            Close
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-medium mb-1">Audit call failed</div>
            <pre className="font-mono text-xs whitespace-pre-wrap break-words text-red-900">
              {error}
            </pre>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-red-700 hover:text-red-900 underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {result && (
        <div className="space-y-3 border-t pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Refs scanned" value={result.refsScanned} tone="gray" />
            <Stat label="Without calibre" value={result.refsWithoutCalibre} tone="gray" />
            <Stat label="Resolved" value={result.refsResolved} tone="brand" />
            <Stat
              label="Unresolved"
              value={result.refsUnresolved}
              tone={result.refsUnresolved > 0 ? 'warn' : 'brand'}
            />
          </div>

          {view === 'pivot' && (
            byBrandSorted.length > 0 ? (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Unresolved by brand — click a row to drill into the detail view
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[28rem] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Unresolved refs</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Distinct calibres</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {byBrandSorted.map((b) => (
                          <tr
                            key={b.brandId}
                            onClick={() => { setBrandFilter(b.brandId); setView('detail'); }}
                            className="cursor-pointer hover:bg-brand-50"
                          >
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {b.brandDisplayName}{' '}
                              <span className="text-xs text-gray-400 font-mono">{b.brandId}</span>
                            </td>
                            <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-700">{b.totalUnresolved}</td>
                            <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-700">{b.distinctCalibres}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                No unresolved calibre strings — every ref's{' '}
                <code className="font-mono">calibre</code> matches an entry in{' '}
                <code className="font-mono">custom_calibres</code> with a{' '}
                <code className="font-mono">manufacturerId</code>.
              </div>
            )
          )}

          {view === 'detail' && (
            <div>
              <div className="flex items-baseline gap-2 flex-wrap mb-2">
                <label className="text-xs font-medium text-gray-700">Brand filter:</label>
                <select
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                >
                  <option value="">— All brands —</option>
                  {byBrandSorted.map((b) => (
                    <option key={b.brandId} value={b.brandId}>
                      {b.brandDisplayName} ({b.totalUnresolved})
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">
                  {filteredRows.length} row{filteredRows.length === 1 ? '' : 's'}
                  {result.rows.length >= 500 ? ' (top 500)' : ''}
                </span>
                {actionedKeys.size > 0 && (
                  <span className="text-xs text-gray-500">
                    · {actionedKeys.size} actioned this session — Re-run Audit for fresh counts
                  </span>
                )}
              </div>
              {filteredRows.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[28rem] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Calibre string</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">Count</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {filteredRows.map((row) => {
                          const key = `${row.brandId}::${normalizeCalibreKey(row.calibreString)}`;
                          const actioned = actionedKeys.has(key);
                          return (
                            <tr key={key} className={actioned ? 'bg-gray-50 opacity-60' : undefined}>
                              <td className="px-3 py-2 text-sm text-gray-700">
                                {row.brandDisplayName}
                              </td>
                              <td className="px-3 py-2 text-sm font-mono text-gray-900 break-all">
                                {row.calibreString}
                                {actioned && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 align-middle">
                                    actioned
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-700">
                                {row.refCount}
                              </td>
                              <td className="px-3 py-2">
                                <CalibreActionToolbar
                                  calibreName={row.calibreString}
                                  brandName={row.brandDisplayName}
                                  brandId={row.brandId}
                                  defaultAkaScope="brand"
                                  showCreateNew={true}
                                  compact={true}
                                  disabled={running || actioned}
                                  onCalibreCreated={() => markActioned(row.brandId, row.calibreString)}
                                  onAliasAdded={() => markActioned(row.brandId, row.calibreString)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  No rows for this filter.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AuditCalibresUnresolvedBrandIdPanel({ onClosed }: { onClosed: () => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CalibresUnresolvedBrandIdAuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Rows the user has acted on this session.
  const [actionedIds, setActionedIds] = useState<Set<string>>(new Set());

  const runAudit = useCallback(async () => {
    setRunning(true);
    setError(null);
    setActionedIds(new Set());
    try {
      const r = await callAuditCalibresWithUnresolvedBrandId();
      setResult(r);
    } catch (err) {
      console.error('Audit failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Audit failed');
    } finally {
      setRunning(false);
    }
  }, []);

  const writeBrandIdToCalibre = useCallback(async (calibreId: string, brandId: string | null) => {
    try {
      await updateDoc(doc(db, 'custom_calibres', calibreId), {
        brandId: brandId,
        brandName: null,
        updatedAt: serverTimestamp(),
      });
      setActionedIds((prev) => {
        const next = new Set(prev);
        next.add(calibreId);
        return next;
      });
      toast.success(brandId ? `Mapped to ${brandId}` : 'Cleared brandId');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      toast.error(msg);
    }
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Read-only scan of <code className="font-mono">custom_calibres</code> for
        docs whose <code className="font-mono">brandId</code> is empty AND{' '}
        <code className="font-mono">brandName</code> is non-empty. These are
        legacy free-text brand values the backfill could not resolve. Map them
        to a watchBrand, clear them, or accept a suggestion.
      </p>

      <div className="flex gap-2">
        <Button onClick={runAudit} loading={running} disabled={running}>
          {result ? 'Re-run Audit' : 'Run Audit'}
        </Button>
        <div className="ml-auto">
          <Button variant="secondary" onClick={onClosed} disabled={running}>
            Close
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <div className="font-medium mb-1">Audit call failed</div>
          <pre className="font-mono text-xs whitespace-pre-wrap break-words text-red-900">{error}</pre>
        </div>
      )}

      {result && (
        <div className="space-y-3 border-t pt-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Scanned" value={result.scanned} tone="gray" />
            <Stat label="Already resolved" value={result.alreadyResolved} tone="brand" />
            <Stat
              label="Unresolved"
              value={result.unresolved}
              tone={result.unresolved > 0 ? 'warn' : 'brand'}
            />
          </div>

          {result.rows.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[28rem] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Calibre</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Legacy brand</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Suggestion</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {result.rows.map((row) => {
                      const actioned = actionedIds.has(row.calibreId);
                      return (
                        <tr key={row.calibreId} className={actioned ? 'bg-gray-50 opacity-60' : undefined}>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {row.calibreDisplayName}{' '}
                            <span className="text-xs text-gray-400 font-mono">{row.calibreId}</span>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-amber-50 text-amber-800 border border-amber-200">
                              {row.brandIdRaw || row.brandNameLegacy}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {row.suggestedBrandId ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-emerald-50 text-emerald-800 border border-emerald-200">
                                {row.suggestedBrandDisplayName || row.suggestedBrandId}
                                <span className="ml-1 text-emerald-600 font-mono">{row.suggestedBrandId}</span>
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">none</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <div className="flex gap-2 flex-wrap">
                              {row.suggestedBrandId && (
                                <button
                                  type="button"
                                  disabled={actioned}
                                  onClick={() => writeBrandIdToCalibre(row.calibreId, row.suggestedBrandId!)}
                                  className="text-emerald-700 hover:text-emerald-900 underline disabled:opacity-50"
                                >
                                  Accept
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={actioned}
                                onClick={() => {
                                  const target = window.prompt(
                                    `Enter watchBrand doc id to map "${row.calibreDisplayName}" → brand:\n` +
                                      `(was "${row.brandIdRaw || row.brandNameLegacy}")`,
                                    row.suggestedBrandId || '',
                                  );
                                  if (target && target.trim()) {
                                    void writeBrandIdToCalibre(row.calibreId, target.trim());
                                  }
                                }}
                                className="text-brand-700 hover:text-brand-900 underline disabled:opacity-50"
                              >
                                Map…
                              </button>
                              <button
                                type="button"
                                disabled={actioned}
                                onClick={() => {
                                  if (window.confirm(`Clear brand from "${row.calibreDisplayName}"?`)) {
                                    void writeBrandIdToCalibre(row.calibreId, null);
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-800 underline disabled:opacity-50"
                              >
                                Clear
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              No unresolved brandId values — every calibre with a brand is mapped.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PushAlgoliaSettingsPanel({ onClosed }: { onClosed: () => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<PushAlgoliaSettingsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runPush = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await callPushAlgoliaRefIndexSettings();
      setResult(r);
      toast.success(
        r.changed
          ? 'Algolia index settings updated'
          : 'Algolia index settings already up to date',
      );
    } catch (err) {
      console.error('Push Algolia settings failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Push failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Declares which fields are facetable on the Algolia refs index. Idempotent
        — safe to re-run after deploying functions that add new denormalized
        fields.
      </p>

      <div className="flex gap-2">
        <Button onClick={runPush} loading={running} disabled={running}>
          Run
        </Button>
        <div className="ml-auto">
          <Button variant="secondary" onClick={onClosed} disabled={running}>
            Close
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-medium mb-1">Push settings call failed</div>
            <pre className="font-mono text-xs whitespace-pre-wrap break-words text-red-900">
              {error}
            </pre>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-red-700 hover:text-red-900 underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {result && (
        <div className="space-y-3 border-t pt-4">
          <div
            className={`rounded-lg p-3 text-sm font-medium ${
              result.changed
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-gray-50 border border-gray-200 text-gray-700'
            }`}
          >
            {result.changed
              ? '✓ Settings changed — Algolia index updated'
              : 'No change — settings already match'}
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              attributesForFaceting ({result.attributesForFaceting.length})
            </div>
            <div className="border rounded-lg bg-gray-50 max-h-80 overflow-auto">
              <pre className="font-mono text-xs text-gray-800 p-3 whitespace-pre-wrap break-all">
                {result.attributesForFaceting.length > 0
                  ? result.attributesForFaceting.join('\n')
                  : '(empty)'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// --- Audit: aliasDetails[].brandId shape ------------------------------------
// Legacy AKAs sometimes store a free-text display name (e.g. "Bell & Ross") in
// aliasDetails[].brandId instead of the watchBrands slug ("bell-ross"). When
// that happens the resolver builds the scoped cache key from the slug on the
// reference side but from the legacy text on the calibre side, so the two keys
// never match and the brand-scoped AKA silently fails to canonicalize. This
// client-side tool finds those rows, proposes slug replacements by matching the
// legacy text against every brand's name/displayName/id (case- and
// punctuation-insensitive), and rewrites only the auto-fixable ones. Universal
// AKAs (no brandId) and already-valid slugs are left untouched, so it is safe
// to re-run.

// Mirror of normalizeBrandKey in functions/src/index.ts so client-side
// proposals match what the server-side resolver would map a value to.
function normalizeAkaBrandKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

type AkaShapeStatus = 'fixable' | 'ambiguous' | 'orphan';

interface AkaShapeRow {
  calibreId: string;
  calibreName: string;
  aliasIndex: number;
  aliasName: string;
  currentBrandId: string;
  status: AkaShapeStatus;
  proposedBrandId?: string;
  proposedBrandDisplay?: string;
  candidates: { id: string; display: string }[];
}

interface AkaShapeScan {
  calibresScanned: number;
  aliasesScanned: number;
  validAkas: number; // brandId present and already a valid slug
  universalAkas: number; // no brandId (universal AKA, left untouched)
  rows: AkaShapeRow[]; // suspect rows only
}

function scanAkaBrandIds(
  calibres: CustomCalibreEntry[],
  brands: WatchBrand[],
): AkaShapeScan {
  const validIds = new Set(brands.map((b) => b.id));
  const displayById = new Map<string, string>();
  // normalized key -> set of brand doc ids that share that key
  const idsByKey = new Map<string, Set<string>>();
  const addKey = (key: string, id: string) => {
    if (!key) return;
    let set = idsByKey.get(key);
    if (!set) {
      set = new Set();
      idsByKey.set(key, set);
    }
    set.add(id);
  };
  for (const b of brands) {
    const display =
      (b.displayName && b.displayName.trim()) ||
      (b.name && b.name.trim()) ||
      b.id;
    displayById.set(b.id, display);
    addKey(normalizeAkaBrandKey(b.id), b.id);
    addKey(normalizeAkaBrandKey(b.name ?? ''), b.id);
    addKey(normalizeAkaBrandKey(b.displayName ?? ''), b.id);
  }

  let aliasesScanned = 0;
  let validAkas = 0;
  let universalAkas = 0;
  const rows: AkaShapeRow[] = [];

  for (const c of calibres) {
    const details = c.aliasDetails ?? [];
    details.forEach((ad, i) => {
      aliasesScanned++;
      const raw = typeof ad.brandId === 'string' ? ad.brandId.trim() : '';
      if (!raw) {
        universalAkas++; // universal AKA — no brand scope, untouched
        return;
      }
      if (validIds.has(raw)) {
        validAkas++; // already a valid watchBrands slug
        return;
      }
      // Suspect: truthy brandId that is not a known watchBrands doc id.
      const matches = idsByKey.get(normalizeAkaBrandKey(raw));
      const candidateIds = matches ? Array.from(matches) : [];
      let status: AkaShapeStatus;
      let proposedBrandId: string | undefined;
      if (candidateIds.length === 1) {
        status = 'fixable';
        proposedBrandId = candidateIds[0];
      } else if (candidateIds.length > 1) {
        status = 'ambiguous';
      } else {
        status = 'orphan';
      }
      rows.push({
        calibreId: c.id,
        calibreName: c.name,
        aliasIndex: i,
        aliasName: ad.name,
        currentBrandId: raw,
        status,
        proposedBrandId,
        proposedBrandDisplay: proposedBrandId
          ? displayById.get(proposedBrandId)
          : undefined,
        candidates: candidateIds.map((id) => ({
          id,
          display: displayById.get(id) || id,
        })),
      });
    });
  }

  rows.sort(
    (a, b) =>
      a.calibreName.localeCompare(b.calibreName) ||
      a.aliasName.localeCompare(b.aliasName),
  );

  return {
    calibresScanned: calibres.length,
    aliasesScanned,
    validAkas,
    universalAkas,
    rows,
  };
}

function AuditAkaBrandIdShapePanel({ onClosed }: { onClosed: () => void }) {
  const [running, setRunning] = useState(false);
  const [scan, setScan] = useState<AkaShapeScan | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Keep the raw calibre docs so Apply can rebuild aliasDetails in place.
  const [calibresById, setCalibresById] = useState<Map<string, CustomCalibreEntry>>(
    new Map(),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ fixed: number; skipped: number } | null>(
    null,
  );
  const [resyncRunning, setResyncRunning] = useState(false);
  const [resyncDone, setResyncDone] = useState(false);
  const [editEntry, setEditEntry] = useState<CustomCalibreEntry | null>(null);

  const fixableRows = useMemo(
    () => (scan ? scan.rows.filter((r) => r.status === 'fixable') : []),
    [scan],
  );
  const ambiguousCount = useMemo(
    () => (scan ? scan.rows.filter((r) => r.status === 'ambiguous').length : 0),
    [scan],
  );
  const orphanCount = useMemo(
    () => (scan ? scan.rows.filter((r) => r.status === 'orphan').length : 0),
    [scan],
  );

  const runScan = useCallback(async () => {
    setRunning(true);
    setError(null);
    setApplyResult(null);
    setResyncDone(false);
    try {
      const [calibres, brands] = await Promise.all([
        getCustomCalibres(true), // force server read so we scan live data
        getWatchBrands(),
      ]);
      setCalibresById(new Map(calibres.map((c) => [c.id, c])));
      setScan(scanAkaBrandIds(calibres, brands));
    } catch (err) {
      console.error('AKA brandId shape scan failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setRunning(false);
    }
  }, []);

  const applyFixes = useCallback(async () => {
    setApplying(true);
    setError(null);
    try {
      // Group auto-fixable rows by calibre so each doc is written once.
      const byCalibre = new Map<string, AkaShapeRow[]>();
      for (const r of fixableRows) {
        const arr = byCalibre.get(r.calibreId) ?? [];
        arr.push(r);
        byCalibre.set(r.calibreId, arr);
      }
      let fixed = 0;
      for (const [calibreId, calRows] of byCalibre) {
        const calibre = calibresById.get(calibreId);
        if (!calibre?.aliasDetails) continue;
        const proposedByIndex = new Map<number, string>();
        for (const r of calRows) {
          if (r.proposedBrandId) proposedByIndex.set(r.aliasIndex, r.proposedBrandId);
        }
        // Rewrite only brandId on the targeted entries; preserve name,
        // showInExplore, and any other fields on every entry.
        const next: CalibreAliasDetail[] = calibre.aliasDetails.map((ad, i) => {
          const proposed = proposedByIndex.get(i);
          return proposed ? { ...ad, brandId: proposed } : ad;
        });
        await updateDoc(doc(db, 'custom_calibres', calibreId), {
          aliasDetails: next,
          updatedAt: serverTimestamp(),
        });
        fixed += proposedByIndex.size;
      }
      const skipped = (scan?.rows.length ?? 0) - fixed;
      setApplyResult({ fixed, skipped });
      setConfirmOpen(false);
      toast.success(`Fixed ${fixed} AKA brandId${fixed === 1 ? '' : 's'}`);
      // Re-scan from server so the table reflects the writes and shows 0 fixable.
      await runScan();
    } catch (err) {
      console.error('Apply AKA brandId fixes failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [fixableRows, calibresById, scan, runScan]);

  const runResync = useCallback(async () => {
    setResyncRunning(true);
    setError(null);
    try {
      await callResyncAllReferencesToAlgolia();
      setResyncDone(true);
      toast.success('Resync triggered');
    } catch (err) {
      console.error('Resync failed', err);
      const msg = (err as { message?: string })?.message || String(err);
      setError(msg);
      toast.error(err instanceof Error ? err.message : 'Resync failed');
    } finally {
      setResyncRunning(false);
    }
  }, []);

  const statusBadge = (status: AkaShapeStatus) => {
    if (status === 'fixable')
      return <Badge variant="success">✓ auto-fixable</Badge>;
    if (status === 'ambiguous')
      return <Badge variant="warning">⚠ ambiguous</Badge>;
    return <Badge variant="danger">✗ orphan</Badge>;
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Client-side scan of every <code className="font-mono">aliasDetails[].brandId</code>{' '}
        on <code className="font-mono">custom_calibres</code>. Flags entries whose{' '}
        <code className="font-mono">brandId</code> is truthy but is{' '}
        <strong>not</strong> a known <code className="font-mono">watchBrands</code> doc id —
        i.e. legacy free-text like <code className="font-mono">&quot;Bell &amp; Ross&quot;</code>{' '}
        instead of the slug <code className="font-mono">bell-ross</code>. Universal AKAs
        (no <code className="font-mono">brandId</code>) and already-valid slugs are left
        untouched. <strong>Apply fixes</strong> rewrites only the auto-fixable rows;
        ambiguous and orphan rows are skipped. Safe to re-run.
      </p>

      <div className="flex gap-2">
        <Button onClick={runScan} loading={running} disabled={running || applying}>
          {scan ? 'Re-run dry run' : 'Run dry run'}
        </Button>
        <Button
          variant="primary"
          onClick={() => setConfirmOpen(true)}
          disabled={running || applying || !scan || fixableRows.length === 0}
        >
          Apply fixes ({fixableRows.length})
        </Button>
        <div className="ml-auto">
          <Button variant="secondary" onClick={onClosed} disabled={running || applying}>
            Close
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-medium mb-1">Operation failed</div>
            <pre className="font-mono text-xs whitespace-pre-wrap break-words text-red-900">
              {error}
            </pre>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-red-700 hover:text-red-900 underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {applyResult && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-900 space-y-3">
          <div>
            <strong>{applyResult.fixed}</strong> AKA brandId
            {applyResult.fixed === 1 ? '' : 's'} fixed,{' '}
            <strong>{applyResult.skipped}</strong> skipped (ambiguous / orphan).
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={runResync}
              loading={resyncRunning}
              disabled={resyncRunning || resyncDone}
            >
              {resyncDone ? 'Resync triggered ✓' : 'Resync references to Algolia'}
            </Button>
            <span className="text-xs text-green-800">
              {resyncDone
                ? 'The corrected scoped AKAs will take effect once the resync completes.'
                : 'Heavy operation — run once, deliberately, after the fixes land.'}
            </span>
          </div>
        </div>
      )}

      {scan && (
        <div className="space-y-3 border-t pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Calibres scanned" value={scan.calibresScanned} tone="gray" />
            <Stat label="AKAs scanned" value={scan.aliasesScanned} tone="gray" />
            <Stat label="Universal (no brandId)" value={scan.universalAkas} tone="gray" />
            <Stat label="Valid slug" value={scan.validAkas} tone="gray" />
            <Stat
              label="Auto-fixable"
              value={fixableRows.length}
              tone={fixableRows.length > 0 ? 'warn' : 'brand'}
            />
            <Stat
              label="Ambiguous / orphan"
              value={ambiguousCount + orphanCount}
              tone={ambiguousCount + orphanCount > 0 ? 'warn' : 'brand'}
            />
          </div>

          {scan.rows.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[28rem] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Calibre
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        AKA name
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Current brandId
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Proposed brandId
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {scan.rows.map((row) => (
                      <tr key={`${row.calibreId}::${row.aliasIndex}`}>
                        <td className="px-3 py-2 text-sm">
                          <button
                            type="button"
                            onClick={() => {
                              const entry = calibresById.get(row.calibreId);
                              if (entry) setEditEntry(entry);
                            }}
                            className="text-brand-600 hover:text-brand-800 underline text-left"
                          >
                            {row.calibreName}
                          </button>
                          <div className="text-[11px] text-gray-400 font-mono break-all">
                            {row.calibreId}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">{row.aliasName}</td>
                        <td className="px-3 py-2 text-sm font-mono text-gray-900 break-all">
                          {row.currentBrandId}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {row.status === 'fixable' ? (
                            <span className="font-mono text-gray-900 break-all">
                              {row.proposedBrandId}
                              {row.proposedBrandDisplay && (
                                <span className="text-gray-400 font-sans">
                                  {' '}
                                  ({row.proposedBrandDisplay})
                                </span>
                              )}
                            </span>
                          ) : row.status === 'ambiguous' ? (
                            <span className="text-amber-800">
                              ambiguous —{' '}
                              <span className="font-mono break-all">
                                {row.candidates.map((c) => c.id).join(', ')}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">no match</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">{statusBadge(row.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
              No suspect AKA brandIds found — every{' '}
              <code className="font-mono">aliasDetails[].brandId</code> is either empty
              (universal) or already a valid <code className="font-mono">watchBrands</code>{' '}
              slug. Nothing to fix.
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog before writing */}
      <Modal
        isOpen={confirmOpen}
        onClose={() => !applying && setConfirmOpen(false)}
        title="Apply AKA brandId fixes"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1">
            <div>
              This will rewrite <strong>{fixableRows.length}</strong> auto-fixable AKA
              brandId{fixableRows.length === 1 ? '' : 's'} across{' '}
              <strong>
                {new Set(fixableRows.map((r) => r.calibreId)).size}
              </strong>{' '}
              calibre doc
              {new Set(fixableRows.map((r) => r.calibreId)).size === 1 ? '' : 's'}.
            </div>
            <div>
              Ambiguous ({ambiguousCount}) and orphan ({orphanCount}) rows are{' '}
              <strong>skipped</strong>. Only <code className="font-mono">brandId</code> is
              changed; all other alias fields are preserved.
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={applying}
            >
              Cancel
            </Button>
            <Button onClick={applyFixes} loading={applying} disabled={applying}>
              Apply {fixableRows.length} fix{fixableRows.length === 1 ? '' : 'es'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Calibre edit dialog, launched from a row's calibre link */}
      <CustomCalibreModal
        isOpen={editEntry !== null}
        onClose={() => setEditEntry(null)}
        initialEditEntry={editEntry}
        onSaved={() => {
          setEditEntry(null);
          void runScan();
        }}
      />
    </div>
  );
}
